import React, { useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, UserRole } from '../contexts/AuthContext';
import sulibarLogo from '../assets/images/sulibari1.png';

// Define menu items for different roles
interface MenuItem {
  nameKey: string;
  path: string;
  icon: React.ReactNode;
  allowedRoles: UserRole[];
}

const Layout: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { userData, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Menu items with role-based access control
  const menuItems: MenuItem[] = [
    {
      nameKey: 'common.dashboard',
      path: '/dashboard',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      ),
      allowedRoles: ['admin', 'teacher', 'parent', 'student'],
    },
    {
      nameKey: 'common.students',
      path: '/students',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      allowedRoles: ['admin', 'teacher'],
    },
    {
      nameKey: 'common.classes',
      path: '/classes',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      allowedRoles: ['admin', 'teacher'],
    },
    {
      nameKey: 'common.content',
      path: '/content',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      allowedRoles: ['admin', 'teacher'],
    },
    {
      nameKey: 'common.reports',
      path: '/reports',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      allowedRoles: ['admin', 'teacher', 'parent'],
    },
    {
      nameKey: 'common.users',
      path: '/users',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      allowedRoles: ['admin'],
    },
    {
      nameKey: 'common.settings',
      path: '/settings',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      allowedRoles: ['admin'],
    },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter((item) => 
    userData && item.allowedRoles.includes(userData.role)
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`${
          isMenuOpen ? 'block' : 'hidden'
        } md:block bg-white shadow-lg md:w-64 w-full fixed md:relative z-20 flex flex-col h-full`}
      >
        <div className="flex items-center justify-center h-20 border-b flex-shrink-0">
          <img src={sulibarLogo} alt="Sulibari Logo" className="h-12 object-contain" />
        </div>
        
        {/* Navigation - add pb-32 to ensure space for the fixed profile section */}
        <div className="flex-1 overflow-y-auto pb-32">
          <nav className="px-4 py-4">
            <ul className="space-y-2">
              {filteredMenuItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center p-3 text-gray-700 rounded-lg hover:bg-gray-100 ${
                      location.pathname === item.path ? 'bg-gray-100 text-[#309D97]' : ''
                    }`}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span>{t(item.nameKey)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        
        {/* User Profile & Logout - absolute positioned at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t bg-white">
          <div className="p-4">
            <div className="flex items-center p-2 mb-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#309D97] flex items-center justify-center text-white mr-3 flex-shrink-0">
                {userData?.displayName?.[0]?.toUpperCase() || userData?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="overflow-hidden">
                <div className="text-sm font-medium text-gray-700 truncate">{userData?.displayName || userData?.email}</div>
                <div className="text-xs text-gray-500 capitalize">
                  {userData?.role ? t(`roles.${userData.role}`) : ''}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex w-full items-center p-3 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b flex items-center justify-between p-4">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center md:hidden">
            <img src={sulibarLogo} alt="Sulibari Logo" className="h-8 object-contain" />
          </div>
          {/* Removed user profile from header as it's now in the sidebar */}
          <div className="w-8 h-8"></div> {/* Empty div to maintain layout */}
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 bg-gray-100">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 